// DFUInstructions.js - DFU 模式命令行指引组件

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import GetAppIcon from '@material-ui/icons/GetApp';

const useStyles = makeStyles((theme) => ({
  commandBox: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    fontSize: '14px',
    position: 'relative',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    overflowX: 'auto',
  },
  copyButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: '#d4d4d4',
  },
  stepContent: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  downloadButton: {
    marginTop: theme.spacing(2),
  },
}));

export default function DFUInstructions({ open, onClose, firmwareFile }) {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const firmwareFileName = firmwareFile?.name || 'firmware.bin';
  const dfuCommand = `dfu-util -a 0 -s 0x08000000:leave -D ${firmwareFileName}`;

  const handleCopyCommand = () => {
    navigator.clipboard.writeText(dfuCommand);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadFirmware = () => {
    if (!firmwareFile) return;

    // 创建下载链接
    const url = URL.createObjectURL(firmwareFile);
    const a = document.createElement('a');
    a.href = url;
    a.download = firmwareFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const steps = [
    {
      label: 'Device is in DFU Mode',
      description: 'Your device has entered DFU (Device Firmware Update) mode and is ready for flashing.',
    },
    {
      label: 'Download Firmware File',
      description: 'Save the firmware file to your computer if you haven\'t already.',
    },
    {
      label: 'Open Terminal',
      description: 'Open a terminal or command prompt on your computer.',
    },
    {
      label: 'Navigate to Firmware Directory',
      description: `Navigate to the directory where you saved ${firmwareFileName}.`,
    },
    {
      label: 'Run DFU Command',
      description: 'Copy and run the command below to flash the firmware.',
    },
    {
      label: 'Wait for Completion',
      description: 'The flashing process will take about 30-60 seconds. Do not disconnect the device!',
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1}>
            DFU Mode - Flash Firmware
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* 警告提示 */}
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          <AlertTitle>⚠️ Important</AlertTitle>
          <Typography variant="body2">
            • Do NOT disconnect the device during the flashing process
          </Typography>
          <Typography variant="body2">
            • Make sure you have dfu-util installed on your system
          </Typography>
          <Typography variant="body2">
            • The device will automatically restart after flashing
          </Typography>
        </Alert>

        {/* 步骤指引 */}
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>
                <Typography variant="body2" className={classes.stepContent}>
                  {step.description}
                </Typography>

                {/* 下载固件按钮 */}
                {index === 1 && firmwareFile && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<GetAppIcon />}
                    onClick={handleDownloadFirmware}
                    className={classes.downloadButton}
                  >
                    Download {firmwareFileName}
                  </Button>
                )}

                {/* DFU 命令 */}
                {index === 4 && (
                  <Paper className={classes.commandBox} elevation={0}>
                    <code>{dfuCommand}</code>
                    <Tooltip title={copied ? "Copied!" : "Copy command"}>
                      <IconButton
                        size="small"
                        className={classes.copyButton}
                        onClick={handleCopyCommand}
                      >
                        {copied ? <CheckCircleIcon /> : <FileCopyIcon />}
                      </IconButton>
                    </Tooltip>
                  </Paper>
                )}

                <Box mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setActiveStep(index + 1)}
                    disabled={index === steps.length - 1}
                  >
                    {index === steps.length - 1 ? 'Done' : 'Next'}
                  </Button>
                  {index > 0 && (
                    <Button
                      onClick={() => setActiveStep(index - 1)}
                      style={{ marginLeft: 8 }}
                    >
                      Back
                    </Button>
                  )}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {/* 完成提示 */}
        {activeStep === steps.length && (
          <Alert severity="success" style={{ marginTop: 16 }}>
            <AlertTitle>✅ Ready to Flash</AlertTitle>
            <Typography variant="body2">
              You're all set! Run the command in your terminal and wait for the process to complete.
            </Typography>
          </Alert>
        )}

        {/* 系统安装指引 */}
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
            Install dfu-util (if not installed):
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            <Paper style={{ padding: 12, backgroundColor: '#f5f5f5' }}>
              <Typography variant="caption" display="block" style={{ fontWeight: 600 }}>
                Linux (Ubuntu/Debian):
              </Typography>
              <code style={{ fontSize: '12px' }}>sudo apt install dfu-util</code>
            </Paper>
            <Paper style={{ padding: 12, backgroundColor: '#f5f5f5' }}>
              <Typography variant="caption" display="block" style={{ fontWeight: 600 }}>
                macOS:
              </Typography>
              <code style={{ fontSize: '12px' }}>brew install dfu-util</code>
            </Paper>
            <Paper style={{ padding: 12, backgroundColor: '#f5f5f5' }}>
              <Typography variant="caption" display="block" style={{ fontWeight: 600 }}>
                Windows:
              </Typography>
              <Typography variant="caption" style={{ fontSize: '12px' }}>
                Download from{' '}
                <a
                  href="http://dfu-util.sourceforge.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2' }}
                >
                  dfu-util.sourceforge.net
                </a>
              </Typography>
            </Paper>
          </Box>
        </Box>

        {/* 故障排除 */}
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
            Troubleshooting:
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            • If dfu-util cannot find the device, try unplugging and re-plugging the device
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            • On Linux, you may need to run with sudo: <code>sudo dfu-util ...</code>
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            • If the device doesn't enter DFU mode, try the upgrade process again
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Close
        </Button>
        <Button
          onClick={handleCopyCommand}
          color="primary"
          variant="contained"
          startIcon={copied ? <CheckCircleIcon /> : <FileCopyIcon />}
        >
          {copied ? 'Copied!' : 'Copy Command'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
